export interface WorldParams {
  seed: number;
  /** Region grid resolution. */
  n: number;
  /** Metres per region cell. */
  cellSize: number;
  /** Metres of elevation per unit of normalised height. */
  heightScale: number;
  /** One landmass ringed by open ocean, rather than a continental edge. */
  island?: boolean;
}

export const DEFAULT_WORLD: WorldParams = {
  seed: 20260808,
  n: 192,
  cellSize: 972,
  heightScale: 2400,
};

/** A ~50 km island: small enough to see whole, big enough to hold watersheds. */
export const ISLAND_WORLD: WorldParams = {
  seed: 1893,
  n: 192,
  cellSize: 260,
  // Peaks around 2.6 km over an island 36 km across. The number is only worth
  // having because the shape underneath it earns it: the region field puts most
  // of its relief at two to six kilometres, so this scales ridges, flanks and
  // ravines rather than inflating one dome.
  heightScale: 3100,
  island: true,
};

export type BiomeKey =
  | 'ocean' | 'beach' | 'marsh' | 'riparian' | 'redwood' | 'oak'
  | 'chaparral' | 'grassland' | 'scrub' | 'desert' | 'conifer' | 'alpine';

export interface Biome {
  key: BiomeKey;
  name: string;
  colour: [readonly [number, number, number], readonly [number, number, number]];
  /** How much vegetation stands on it, 0..1. */
  density: number;
  /** Whether that vegetation is tree-shaped. */
  woody: boolean;
}

/**
 * Ground and foliage colours, as two tones each — the low one and the high one,
 * mixed by a hash of position.
 *
 * These are read under ACES tone mapping at 1.55 exposure, and the pipeline
 * between this table and the screen has more gain in it than it looks: a
 * Lambert surface under midday sun and sky returns about half its albedo, and
 * the exposure multiplies that back by 2.6. An albedo written at 140 therefore
 * lands around 145 of 255 on screen — pale — and everything written above it
 * converges on the same cream, because ACES flattens hardest exactly there.
 *
 * `tools/palette.ts` pushes this table through that whole path and prints what
 * each biome photographs as. It is the instrument these numbers were set with,
 * and it is how the previous table was caught: every biome from coast forest to
 * dry scrub came out between luminance 126 and 141, a fifteen-count spread
 * across the entire island. A redwood forest and a desert were the same colour.
 *
 * So the register here is deliberately low and the separation deliberately
 * wide. Forest is genuinely dark — a closed canopy reflects very little — dry
 * country is olive rather than orange, and `alpine` is dark cold stone. Nothing
 * in this table is bright; the only bright thing in the landscape is the frost
 * band `groundColour` lays over the last four hundred metres of the summits,
 * and it reads as frost precisely because the rock under it does not.
 */
export const BIOMES: Record<BiomeKey, Biome> = {
  ocean:     { key:'ocean',     name:'ocean',           colour:[[16,30,46],[26,50,74]],      density:0,    woody:false },
  beach:     { key:'beach',     name:'shore',           colour:[[118,108,82],[144,133,104]], density:0.10, woody:false },
  marsh:     { key:'marsh',     name:'tidal marsh',     colour:[[54,78,48],[72,96,56]],      density:0.40, woody:false },
  riparian:  { key:'riparian',  name:'riparian forest', colour:[[38,76,44],[54,96,50]],      density:0.85, woody:true  },
  redwood:   { key:'redwood',   name:'coast forest',    colour:[[24,56,38],[38,76,46]],      density:0.95, woody:true  },
  oak:       { key:'oak',       name:'oak woodland',    colour:[[54,72,36],[76,90,42]],      density:0.62, woody:true  },
  chaparral: { key:'chaparral', name:'chaparral',       colour:[[78,80,48],[100,98,58]],     density:0.48, woody:false },
  grassland: { key:'grassland', name:'grassland',       colour:[[96,102,56],[122,124,70]],   density:0.16, woody:false },
  scrub:     { key:'scrub',     name:'coastal scrub',   colour:[[76,86,44],[98,102,54]],     density:0.38, woody:false },
  desert:    { key:'desert',    name:'dry scrub',       colour:[[122,108,72],[144,128,90]],  density:0.08, woody:false },
  conifer:   { key:'conifer',   name:'montane conifer', colour:[[32,64,44],[48,82,52]],      density:0.85, woody:true  },
  alpine:    { key:'alpine',    name:'alpine',          colour:[[74,82,92],[104,112,124]],   density:0.04, woody:false },
};

/** Everything the world knows about one point on it. */
export interface Site {
  height: number;
  rain: number;
  flow: number;
  biome: BiomeKey;
  land: boolean;
}
